import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogTrigger, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, ArchiveRestore, Image as ImageIcon,
  Anchor, Eye, EyeOff,
} from "lucide-react";

const DEFAULT_CHECKLIST_TYPES = ["maintenance", "checkin", "checkout"];

function ImagePicker({ value, onChange, library }) {
  const [tab, setTab] = useState(value && !library.find((l) => l.url === value) ? "url" : "library");
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setTab("library")}
          data-testid="boat-img-tab-library"
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            tab === "library"
              ? "bg-[#0C1824] text-white"
              : "bg-slate-100 text-[#2B3F4D] hover:bg-slate-200"
          }`}
        >
          Bibliotek
        </button>
        <button
          type="button"
          onClick={() => setTab("url")}
          data-testid="boat-img-tab-url"
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            tab === "url"
              ? "bg-[#0C1824] text-white"
              : "bg-slate-100 text-[#2B3F4D] hover:bg-slate-200"
          }`}
        >
          Egen URL
        </button>
        <button
          type="button"
          onClick={() => onChange(null)}
          data-testid="boat-img-tab-default"
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            !value
              ? "bg-[#1DA3B3] text-white"
              : "bg-slate-100 text-[#2B3F4D] hover:bg-slate-200"
          }`}
        >
          Standard
        </button>
      </div>
      {tab === "library" && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {library.map((img) => (
            <button
              key={img.key}
              type="button"
              onClick={() => onChange(img.url)}
              data-testid={`boat-img-pick-${img.key}`}
              className={`relative aspect-video rounded-md overflow-hidden border-2 transition-all ${
                value === img.url
                  ? "border-[#BD984D] ring-2 ring-[#BD984D]/30"
                  : "border-slate-200 hover:border-slate-400"
              }`}
            >
              <img src={img.url} alt={img.label_no} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] px-1.5 py-1 text-left">
                {img.label_no}
              </div>
            </button>
          ))}
        </div>
      )}
      {tab === "url" && (
        <div>
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value || null)}
            placeholder="https://eksempel.no/båt.jpg"
            data-testid="boat-img-url-input"
          />
          {value && (
            <div className="mt-2 aspect-video rounded-md overflow-hidden border border-slate-200">
              <img src={value} alt="forhåndsvisning"
                   className="w-full h-full object-cover"
                   onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BoatEditor({ open, onOpenChange, initial, types, library, onSaved }) {
  const isNew = !initial;
  const [name, setName] = useState(initial?.name || "");
  const [model, setModel] = useState(initial?.model || "Greenline");
  const [lengthFt, setLengthFt] = useState(String(initial?.length_ft ?? "39"));
  const [imageUrl, setImageUrl] = useState(initial?.image_url || null);
  const [applicable, setApplicable] = useState(
    initial?.applicable_checklist_types || [...DEFAULT_CHECKLIST_TYPES],
  );

  useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setModel(initial?.model || "Greenline");
      setLengthFt(String(initial?.length_ft ?? "39"));
      setImageUrl(initial?.image_url || null);
      setApplicable(
        initial?.applicable_checklist_types || [...DEFAULT_CHECKLIST_TYPES],
      );
    }
  }, [open, initial]);

  const toggleType = (key) => {
    setApplicable((arr) =>
      arr.includes(key) ? arr.filter((x) => x !== key) : [...arr, key],
    );
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return toast.error("Skriv inn et navn");
    const lenNum = parseInt(lengthFt, 10);
    if (isNaN(lenNum) || lenNum < 10) return toast.error("Lengde må være et tall");
    const body = {
      name: trimmed,
      model: model.trim() || "Greenline",
      length_ft: lenNum,
      image_url: imageUrl,
      applicable_checklist_types: applicable,
    };
    try {
      if (isNew) {
        await api.post("/boats", body);
        toast.success("Båt opprettet");
      } else {
        await api.patch(`/boats/${initial.id}`, body);
        toast.success("Båt oppdatert");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "Legg til båt" : `Rediger ${initial?.name}`}</DialogTitle>
          <DialogDescription>
            {isNew
              ? "En ny båt opprettes med status «inne» og 100 % batteri. Du kan endre disse senere."
              : "Navn, modell, lengde, bilde og hvilke sjekkliste-typer som gjelder for denne båten."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-3">
            <div>
              <Label>Navn</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="E/Y …"
                data-testid="boat-editor-name"
              />
            </div>
            <div>
              <Label>Modell</Label>
              <Input
                value={model}
                onChange={(e) => setModel(e.target.value)}
                data-testid="boat-editor-model"
              />
            </div>
            <div>
              <Label>Lengde (fot)</Label>
              <Input
                type="number"
                min={10}
                max={200}
                value={lengthFt}
                onChange={(e) => setLengthFt(e.target.value)}
                data-testid="boat-editor-length"
              />
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-1.5">
              <ImageIcon className="w-3.5 h-3.5" /> Bilde
            </Label>
            <div className="mt-1.5">
              <ImagePicker value={imageUrl} onChange={setImageUrl} library={library} />
            </div>
          </div>

          <div>
            <Label>Sjekkliste-typer som gjelder for denne båten</Label>
            <p className="text-xs text-[#2B3F4D] mt-0.5 mb-2">
              Bare sjekkliste-typer som er huket av her kan tildeles denne båten.
              Hvis ingen av typene som auto-utløses ved retur (utsjekk/renhold) er
              huket av, vil ingenting auto-genereres ved ute→inne.
            </p>
            <div className="space-y-2">
              {types.map((t) => {
                const checked = applicable.includes(t.key);
                return (
                  <label
                    key={t.key}
                    data-testid={`boat-editor-type-${t.key}`}
                    className="flex items-start gap-2.5 p-2.5 rounded-md hover:bg-slate-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleType(t.key)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#0C1824] flex items-center gap-2 flex-wrap">
                        {t.label_no}
                        {t.is_builtin && (
                          <Badge className="bg-slate-200 text-slate-700 text-[10px]">Innebygd</Badge>
                        )}
                        {t.auto_trigger_on_return && (
                          <Badge className="bg-[#1DA3B3]/15 text-[#1DA3B3] text-[10px]">Auto ved retur</Badge>
                        )}
                      </div>
                      {t.description && (
                        <div className="text-xs text-[#2B3F4D] mt-0.5">{t.description}</div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button
            onClick={save}
            data-testid="boat-editor-save"
            className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
          >
            {isNew ? "Opprett båt" : "Lagre"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BoatsManager() {
  const [boats, setBoats] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [types, setTypes] = useState([]);
  const [library, setLibrary] = useState([]);
  const [editor, setEditor] = useState({ open: false, boat: null });

  const load = async () => {
    const [b, t, l] = await Promise.all([
      api.get("/boats", { params: { include_archived: showArchived } }),
      api.get("/checklist-types"),
      api.get("/boat-images"),
    ]);
    setBoats(b.data);
    setTypes(t.data);
    setLibrary(l.data);
  };
  useEffect(() => { load(); }, [showArchived]);

  const archive = async (boat) => {
    if (!window.confirm(
      `Arkivere «${boat.name}»? All historikk (bookinger, sjekklister, avvik) bevares, men båten skjules fra listingen.`,
    )) return;
    try {
      await api.delete(`/boats/${boat.id}`);
      toast.success("Båten er arkivert");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke arkivere");
    }
  };

  const restore = async (boat) => {
    try {
      await api.post(`/boats/${boat.id}/restore`);
      toast.success("Båten er gjenopprettet");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gjenopprette");
    }
  };

  const active = boats.filter((b) => !b.archived_at);
  const archived = boats.filter((b) => b.archived_at);

  return (
    <div className="space-y-4" data-testid="boats-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#2B3F4D] max-w-2xl">
          Legg til, rediger eller arkiver båter. Arkivering bevarer all historikk
          – du kan gjenopprette en arkivert båt når som helst.
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowArchived((v) => !v)}
            data-testid="boats-toggle-archived"
            className="border-slate-300 text-[#0C1824]"
          >
            {showArchived ? <><EyeOff className="w-4 h-4 mr-1.5" />Skjul arkiv</> : <><Eye className="w-4 h-4 mr-1.5" />Vis arkiverte</>}
          </Button>
          <Button
            onClick={() => setEditor({ open: true, boat: null })}
            data-testid="boats-add"
            className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Legg til båt
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {active.map((b) => (
          <Card key={b.id} className="p-4 flex items-center gap-4" data-testid={`boats-row-${b.id}`}>
            <div className="w-20 h-14 rounded-md overflow-hidden bg-slate-100 flex-shrink-0">
              <img
                src={b.image_url || "/boat-39-side.jpg"}
                alt={b.name}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.src = "/boat-39-side.jpg"; }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#0C1824] flex items-center gap-2 flex-wrap">
                <Anchor className="w-4 h-4 text-[#1DA3B3]" />
                {b.name}
                <Badge className="bg-[#BD984D]/15 text-[#A57F39] text-[10px]">
                  {b.model} · {b.length_ft}′
                </Badge>
                <Badge
                  className={
                    b.status === "ute"
                      ? "bg-[#BD984D]/90 text-[#0C1824] text-[10px]"
                      : "bg-[#1DA3B3]/15 text-[#1DA3B3] text-[10px]"
                  }
                >
                  {b.status === "ute" ? "Ute" : "Inne"}
                </Badge>
              </div>
              <div className="text-xs text-[#2B3F4D] mt-1">
                Sjekkliste-typer:{" "}
                {(b.applicable_checklist_types || ["maintenance","checkin","checkout"]).join(", ")}
              </div>
            </div>
            <div className="flex gap-1.5 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditor({ open: true, boat: b })}
                data-testid={`boats-edit-${b.id}`}
                className="border-[#0C1824] text-[#0C1824] h-9"
              >
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => archive(b)}
                data-testid={`boats-archive-${b.id}`}
                className="border-red-200 text-red-600 hover:bg-red-50 h-9"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </Card>
        ))}
        {active.length === 0 && (
          <Card className="p-8 text-center text-sm text-[#2B3F4D]">
            Ingen aktive båter ennå – klikk «Legg til båt» for å starte.
          </Card>
        )}
      </div>

      {showArchived && archived.length > 0 && (
        <div className="space-y-2 pt-4">
          <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39]">Arkiv</div>
          {archived.map((b) => (
            <Card
              key={b.id}
              className="p-3 flex items-center justify-between bg-slate-50 opacity-80"
              data-testid={`boats-archived-${b.id}`}
            >
              <div>
                <div className="font-medium text-[#0C1824] line-through">{b.name}</div>
                <div className="text-[11px] text-[#2B3F4D]">
                  Arkivert {b.archived_at ? new Date(b.archived_at).toLocaleString("nb-NO") : ""}
                  {b.archived_by_name && <> · {b.archived_by_name}</>}
                </div>
              </div>
              <Button
                onClick={() => restore(b)}
                size="sm"
                data-testid={`boats-restore-${b.id}`}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
              >
                <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" /> Gjenopprett
              </Button>
            </Card>
          ))}
        </div>
      )}

      <BoatEditor
        open={editor.open}
        onOpenChange={(o) => setEditor({ open: o, boat: editor.boat })}
        initial={editor.boat}
        types={types}
        library={library}
        onSaved={load}
      />
    </div>
  );
}
